import get from 'lodash/get';
import assign from 'lodash/assign';

import striptags from 'striptags';
import analyticsConfig from '../../../../server/config/analytics';
import { resolveHost } from '../../../../utils/url_util';
import {
  buildAssetUrlWithPresets,
  imageSizesLookup,
  changeAssetDomain
} from '../../../../utils/cloudinary_asset_util';

/*
Validate your structured data page here (paste in entire view-source: https://search.google.com/structured-data/testing-tool/u/0/
*/
export default function parsely(context) {
  const {
    postData,
    type,
    homeList,
    categoryHomeList,
    categoryPageData,
    tagHomeList,
    tagPageData,
    authorPageData,
    authorAllFeed,
    personPageData,
    mostCreativePeoplePageData
  } = context;

  const siteConfig = analyticsConfig[context.site];

  if (siteConfig === undefined) {
    return '';
  }

  let topicList = [];
  let articleTitle = '';
  let authorName = '';
  let articleSection = '';
  let thumbnail = '';
  let path = '/';
  let articleDate = '';
  let thumbnailCaption;
  let thumbnailWidth;
  let thumbnailHeight;
  let dateModified;
  const DESKTOP_SIZE = true;

  if (postData) {
    topicList = get(postData, 'tags');
    articleTitle = get(postData, 'title');
    authorName = get(postData, 'author.name');
    articleSection = get(postData, 'categories[0].slug'); // TODO, needs primary tag if exist
    thumbnail = buildAssetUrlWithPresets(
      changeAssetDomain(get(postData, 'featured_image.source')),
      imageSizesLookup.meta,
      DESKTOP_SIZE
    );
    path = `/${get(postData, 'id')}/${get(postData, 'slug')}`;
    articleDate = get(postData, 'structuredData.published');
    dateModified = get(postData, 'structuredData.modified');
    thumbnailCaption = striptags(get(postData, 'featured_image.caption') || '');
    thumbnailWidth = get(postData, 'featured_image.width') || 700;
    thumbnailHeight = get(postData, 'featured_image.height') || 700;
  }

  const rootUrl = `https://www.${context.site}.com`;
  const url = `${rootUrl}${path}`;

  const siteMaps = {
    fastcompany: {
      humanReadable: 'Fast Company',
      logo: {
        url: 'https://assets.fastcompany.com/image/upload/v1486565627/logo.png',
        width: 140,
        height: 20
      }
    },
    fastcodesign: {
      humanReadable: 'Co.Design',
      logo: {
        url:
          'https://assets.fastcompany.com/image/upload/v1485296802/fcweb/codesign-seo.png',
        width: 200,
        height: 200
      }
    },
    fastcocreate: {
      humanReadable: 'Co.Create',
      logo: {
        url: 'https://assets.fastcompany.com/image/upload/v1486565627/logo.png',
        width: 140,
        height: 20
      }
    },
    fastcoexist: {
      humanReadable: 'Co.Exist',
      logo: {
        url: 'https://assets.fastcompany.com/image/upload/v1486565627/logo.png',
        width: 140,
        height: 20
      }
    }
  };

  const siteData = get(siteMaps, context.site);
  const siteName = get(siteData, 'humanReadable');
  const logo = get(siteData, 'logo.url');
  const logoWidth = get(siteData, 'logo.width');
  const logoHeight = get(siteData, 'logo.height');

  let tags = '';

  if (topicList) {
    topicList.forEach((tag, index) => {
      if (index !== topicList.length - 1) {
        tags += `"${tag.name}",`;
      } else {
        tags += `"${tag.name}"`;
      }
    });
  }

  const baseData = {
    '@context': 'https://schema.org'
  };

  const typeLookup = {
    post: {
      '@type': 'NewsArticle',
      headline: articleTitle,
      url,
      mainEntityOfPage: {
        '@id': url,
        '@type': 'WebPage'
      },
      thumbnailUrl: thumbnail,
      dateCreated: articleDate,
      datePublished: articleDate,
      dateModified,
      articleSection,
      creator: authorName,
      author: {
        '@type': 'Person',
        name: authorName
      },
      keywords: [tags],
      image: {
        '@type': 'ImageObject',
        caption: thumbnailCaption,
        url: thumbnail,
        width: thumbnailWidth,
        height: thumbnailHeight
      },
      publisher: {
        '@type': 'Organization',
        name: siteName,
        logo: {
          '@type': 'ImageObject',
          url: logo,
          width: logoWidth,
          height: logoHeight
        }
      }
    },
    homepage: {
      '@type': 'ItemList',
      url,
      itemListElement: homeList.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url:
          item.url ||
            `https://www.${resolveHost(get(item, 'link'))}/${get(
              item,
              'id'
            )}/${get(item, 'slug')}`
      }))
    },
    category: {
      '@type': 'ItemList',
      url: `${url}${get(categoryPageData, 'slug')}`,
      itemListElement: categoryHomeList.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url:
          item.url ||
            `https://www.${resolveHost(get(item, 'link'))}/${get(
              item,
              'id'
            )}/${get(item, 'slug')}`
      }))
    },
    tag: {
      '@type': 'ItemList',
      url: `${url}section/${get(tagPageData, 'slug')}`,
      itemListElement: tagHomeList.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url:
          item.url ||
            `https://www.${resolveHost(get(item, 'link'))}/${get(
              item,
              'id'
            )}/${get(item, 'slug')}`
      }))
    },
    author: {
      '@type': 'ItemList',
      url: `${url}user/${get(authorPageData, 'slug')}`,
      itemListElement: authorAllFeed.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url:
          item.url ||
            `https://www.${resolveHost(get(item, 'link'))}/${get(
              item,
              'id'
            )}/${get(item, 'slug')}`
      }))
    },
    personPage: {
      '@type': 'ItemList',
      url: `${url}person/${get(personPageData, 'data.rows[0].url')}`,
      itemListElement: []
    },
    mostCreativePeoplePage: {
      '@type': 'ItemList',
      url: `${url}person/most-creative-people`,
      itemListElement: (get(mostCreativePeoplePageData, 'data.rows') || [])
        .map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: `${url}person/${item.url}`
        }))
    }
  };

  const JSONMeta = JSON.stringify(assign(baseData, typeLookup[type]));
  return typeLookup[type]
    ? `<script type="application/ld+json">${JSONMeta}</script>`
    : '';
}



// WEBPACK FOOTER //
// ./src/views/components/analytics/trackers/structured_data.js